export const JOB_STATUS = {
  PENDING: 'pending',
  PREPARING: 'preparing',
  QUEUED: 'queued',
  WAITING: 'waiting',
  WORKING: 'working',
  /**
   * `overdue`: the tester opened the testing page (`testerAppStartTime` set)
   * but blew past `responseDeadline + grace`. The job is non-terminal — the
   * tester can still submit late (→ `completed`) or drop (→ `expired`), and
   * the late-submission window auto-lapses to `expired` when the tester JWT
   * expires (`overdueLapsesAt`). Customer-facing UI groups this under
   * "Working" with a yellow stopwatch icon.
   */
  OVERDUE: 'overdue',
  /**
   * `admin_hold`: admin paused an in-flight job to preserve tester work for
   * either a true-terminate (→ `cancelled/admin_terminated`) or a reprocess
   * (→ `completed`). The job is non-terminal — tester attribution is kept,
   * tester loses interactive controls (job shows as "Under Admin Review" in
   * their active list), and balance/credit math fires only at the eventual
   * terminal transition. The prior active status is recoverable from the
   * activity log so admins can also unhold back to where the job was.
   */
  ADMIN_HOLD: 'admin_hold',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
  FAILED: 'failed',
} as const;

export type JobStatus = (typeof JOB_STATUS)[keyof typeof JOB_STATUS];

export type TerminalFailureStatus = 'cancelled' | 'expired' | 'failed';

export type TerminalJobStatus = 'completed' | TerminalFailureStatus;
export type ActiveJobStatus = 'pending' | 'preparing' | 'queued' | 'waiting' | 'working' | 'overdue' | 'admin_hold';

export function isTerminalStatus(status: JobStatus | undefined): status is TerminalJobStatus {
  return (
    status === 'completed' ||
    status === 'cancelled' ||
    status === 'expired' ||
    status === 'failed'
  );
}

export function isActiveStatus(status: JobStatus | undefined): status is ActiveJobStatus {
  return (
    status === 'pending' ||
    status === 'preparing' ||
    status === 'queued' ||
    status === 'waiting' ||
    status === 'working' ||
    status === 'overdue' ||
    status === 'admin_hold'
  );
}

/**
 * Whether the tester is allowed to interact with the job (submit, end, drop).
 * `admin_hold` is non-terminal active but tester interaction is suspended
 * pending the admin's next decision — the job stays attributed and visible
 * in the tester's active list, just read-only.
 */
export function isTesterInteractiveStatus(status: JobStatus | undefined): boolean {
  return isActiveStatus(status) && status !== 'admin_hold';
}

export type JobTimeoutType = 'no_claim' | 'no_response' | 'went_silent';

/**
 * Unified modifier vocabulary for the `modifier` column. The discriminated
 * union {@link JobOutcome} pins which modifiers are valid for which
 * `status`.
 */
export type JobModifier =
  // completed
  | 'late_submission'
  // overdue — deprecated (#4064). The value was vestigial: `overdue` had a
  // single allowed modifier, so it carried zero information. New rows leave
  // the column null. Kept in the union so historical rows still type-check
  // until the drain window (≤ 24h, the late-submission window) elapses.
  | 'tester_overtime'
  // expired
  | 'tester_dropped'
  | 'tester_unresponsive'
  | 'no_capacity'
  // cancelled
  /**
   * `tester_cancellation` is the canonical (post-#4079) value — chosen so the
   * display label leads with the disambiguating word ("Tester …") rather
   * than the redundant base status ("Cancelled …"), which matters once the
   * inline modifier chip is truncated to a fixed width. New writers produce
   * this value; readers accept both `tester_cancellation` and the legacy
   * `tester_cancelled` for the duration of the rolling rename.
   */
  | 'tester_cancellation'
  /** @deprecated Use `'tester_cancellation'`. Legacy alias — only on rows written before #4079. */
  | 'tester_cancelled'
  | 'tester_invalidated'
  | 'admin_terminated'
  // failed
  | 'platform_issue'
  | 'system_error'
  | 'ai_extraction_failed'
  | 'ai_extraction_incomplete'
  | 'unreachable_url'
  | 'safety_check_failed';

/**
 * The single source of truth for a job's outcome. Pairs `status` with the
 * subset of `modifier` values valid for that status, so exhaustive `switch`
 * over `outcome.status` narrows `outcome.modifier` automatically.
 */
export type JobOutcome =
  | { readonly status: 'pending' | 'preparing' | 'queued' | 'waiting' | 'working' | 'overdue' | 'admin_hold' }
  | { readonly status: 'completed'; readonly modifier?: 'late_submission' }
  | {
      readonly status: 'expired';
      readonly modifier:
        | 'tester_dropped'
        | 'tester_unresponsive'
        | 'no_capacity';
    }
  | {
      readonly status: 'cancelled';
      readonly modifier:
        // `tester_cancellation` is the canonical post-#4079 value; the
        // legacy `tester_cancelled` is kept until the chore migration
        // backfills historical rows. Both narrow under the same arm so
        // exhaustive switches over `outcome.status` continue to compile.
        | 'tester_cancellation'
        | 'tester_cancelled'
        | 'tester_invalidated'
        | 'admin_terminated';
    }
  | {
      readonly status: 'failed';
      readonly modifier:
        | 'platform_issue'
        | 'system_error'
        | 'ai_extraction_failed'
        | 'ai_extraction_incomplete'
        | 'unreachable_url'
        | 'safety_check_failed';
    };

/**
 * `JobModifier` with every legacy alias collapsed onto its canonical name.
 * Currently only `tester_cancelled` is non-canonical; `canonicalModifier`
 * folds it into `tester_cancellation`. Use this when you need a single
 * value to switch on or compare against — display labels, equality checks,
 * external API output, etc.
 */
export type CanonicalJobModifier = Exclude<JobModifier, 'tester_cancelled'>;

export function canonicalModifier(modifier: JobModifier): CanonicalJobModifier {
  switch (modifier) {
    case 'tester_cancelled': return 'tester_cancellation';
    default:                 return modifier;
  }
}

/**
 * Customer billing fires only on terminal `completed` (the only outcome
 * that delivered a result). Every other terminal is zero-charge. `overdue`
 * is non-terminal and never reaches this gate — its terminal exit
 * (`completed` via late-submit, or `expired/tester_dropped` via drop/lapse)
 * is what the gate sees.
 */
export function isZeroChargeOutcome(status: JobStatus): boolean {
  return status !== 'completed';
}

